import * as THREE from 'three';
import { SLIME_COLOR, SLIME_EMISSIVE, type SlimeStyle } from './Slime';
import type { MovingPlatform } from '../world/CollisionWorld';

function makeGooMaterial(opacity: number): THREE.MeshPhysicalMaterial {
  return new THREE.MeshPhysicalMaterial({
    color: SLIME_COLOR,
    roughness: 0.12,
    metalness: 0,
    transmission: 0.35,
    thickness: 0.6,
    clearcoat: 0.6,
    clearcoatRoughness: 0.2,
    emissive: SLIME_EMISSIVE,
    emissiveIntensity: 0.35,
    transparent: true,
    opacity,
    depthWrite: false,
    side: THREE.DoubleSide,
  });
}

function applyGooStyle(m: THREE.MeshPhysicalMaterial, style: SlimeStyle): void {
  if (style === 'cartoon') {
    m.transparent = false;
    m.opacity = 1;
    m.transmission = 0;
  } else {
    m.transparent = true;
    m.opacity = 0.92;
    m.transmission = 0.35;
  }
  m.needsUpdate = true;
}

export interface SlopeContact {
  height: number;
  normalX: number;
  normalY: number;
  normalZ: number;
}

export type GroundSampler = (x: number, z: number) => SlopeContact;

/** Jalur jejak menempel ke platform bergerak (ikuti platform). */
export type PlatformUnder = (x: number, z: number) => MovingPlatform | null;

interface Pt {
  x: number;
  y: number;
  z: number;
  age: number;
  brk: boolean;
  nx: number;
  ny: number;
  nz: number;
  /** Bila titik ditinggalkan DI ATAS platform bergerak: ikut platform. */
  anchor: { platform: MovingPlatform; offX: number; offZ: number } | null;
  /** Lebar titik relatif (1 = diameter slime). >1 = jejak melebar. */
  w: number;
}

const SEG_GAP = 0.2;
const THICK = 0.03;
const ARC = 10;

class Ribbon {
  readonly mesh: THREE.Mesh;
  readonly geo: THREE.BufferGeometry;
  mat: THREE.Material;
  readonly points: Pt[] = [];
  thickness = 0.02;
  private style: SlimeStyle = 'transparent';
  private colorHex: number = SLIME_COLOR;

  constructor(scene: THREE.Scene) {
    this.geo = new THREE.BufferGeometry();
    this.mat = makeGooMaterial(0.92);
    this.mesh = new THREE.Mesh(this.geo, this.mat);
    this.mesh.frustumCulled = false;
    // Render SETELAH badan slime (renderOrder 1) dengan depth-test:
    // fragmen trail yang tertutup badan gagal uji depth -> jejak tidak
    // terlihat menembus slime transparan.
    this.mesh.renderOrder = 2;
    scene.add(this.mesh);
  }

  setMode(mode: 'flat' | '3d'): void {
    (this.mesh.material as THREE.Material).dispose();
    this.thickness = mode === '3d' ? 0.16 : 0.02;
    const mat = makeGooMaterial(0.92);
    this.mesh.material = mat;
    this.mat = mat;
    this.applyStyle();
    this.applyColor();
  }

  setStyle(style: SlimeStyle): void {
    this.style = style;
    this.applyStyle();
  }

  setColor(hex: number): void {
    this.colorHex = hex;
    this.applyColor();
  }

  private applyColor(): void {
    (this.mesh.material as THREE.MeshPhysicalMaterial).color.setHex(this.colorHex);
  }

  private applyStyle(): void {
    applyGooStyle(this.mesh.material as THREE.MeshPhysicalMaterial, this.style);
  }

  rebuild(halfWidth: number, fadeDuration: number): void {
    const pts = this.points;
    const n = pts.length;

    if (n < 2) {
      this.geo.setAttribute('position', new THREE.Float32BufferAttribute([0, 0, 0, 0, 0, 0], 3));
      this.geo.setIndex(null);
      this.geo.setDrawRange(0, 0);
      this.geo.computeBoundingSphere();
      return;
    }

    const p: number[] = [];
    const id: number[] = [];
    const push = (x: number, y: number, z: number): number => {
      p.push(x, y, z);
      return p.length / 3 - 1;
    };

    const segDir = (i: number): { x: number; y: number; z: number } => {
      const a = pts[i];
      const b = pts[Math.min(n - 1, i + 1)];
      let tx = b.x - a.x;
      let ty = b.y - a.y;
      let tz = b.z - a.z;
      let tl = Math.hypot(tx, ty, tz);
      if (tl < 1e-5 && i > 0) {
        const c = pts[i - 1];
        tx = a.x - c.x;
        ty = a.y - c.y;
        tz = a.z - c.z;
        tl = Math.hypot(tx, ty, tz);
      }
      if (tl < 1e-5) return { x: 1, y: 0, z: 0 };
      return { x: tx / tl, y: ty / tl, z: tz / tl };
    };

    const widthAt = (i: number): number => {
      const ageT = pts[i].age / fadeDuration;
      return halfWidth * pts[i].w * Math.max(0, 1 - ageT);
    };

    const WSEG = 6;
    const ring: number[][] = new Array(n);
    const normal = (i: number): { x: number; y: number; z: number } => {
      const nn = pts[i];
      const nx = nn.nx;
      const ny = nn.ny;
      const nz = nn.nz;
      const l = Math.hypot(nx, ny, nz);
      return l > 1e-5 ? { x: nx / l, y: ny / l, z: nz / l } : { x: 0, y: 1, z: 0 };
    };
    const thk = this.thickness;

    for (let i = 0; i < n; i++) {
      const t = segDir(i);
      const nrm = normal(i);
      const rx = nrm.y * t.z - nrm.z * t.y;
      const ry = nrm.z * t.x - nrm.x * t.z;
      const rz = nrm.x * t.y - nrm.y * t.x;
      const rl = Math.hypot(rx, ry, rz) || 1;
      const ux = rx / rl;
      const uy = ry / rl;
      const uz = rz / rl;
      const hw = widthAt(i);
      const rr: number[] = new Array(WSEG);
      for (let k = 0; k < WSEG; k++) {
        const tt = -1 + (2 * k) / (WSEG - 1);
        const lift = thk * (1 - tt * tt);
        rr[k] = push(
          pts[i].x + ux * hw * tt + nrm.x * lift,
          pts[i].y + uy * hw * tt + nrm.y * lift,
          pts[i].z + uz * hw * tt + nrm.z * lift,
        );
      }
      ring[i] = rr;
    }

    for (let i = 0; i < n - 1; i++) {
      if (pts[i + 1].brk) continue;
      const a = ring[i];
      const b = ring[i + 1];
      for (let k = 0; k < WSEG - 1; k++) {
        const p0 = a[k];
        const p1 = a[k + 1];
        const q0 = b[k];
        const q1 = b[k + 1];
        id.push(p0, q1, p1, p0, q0, q1);
      }
    }

    const cap = (i: number, t: { x: number; y: number; z: number }, sign: number): void => {
      const pt = pts[i];
      const nrm = normal(i);
      const rx = nrm.y * t.z - nrm.z * t.y;
      const ry = nrm.z * t.x - nrm.x * t.z;
      const rz = nrm.x * t.y - nrm.y * t.x;
      const rl = Math.hypot(rx, ry, rz) || 1;
      const ux = rx / rl;
      const uy = ry / rl;
      const uz = rz / rl;
      const hw = widthAt(i);
      const center = push(pt.x + nrm.x * thk, pt.y + nrm.y * thk, pt.z + nrm.z * thk);
      const arc: number[] = [];
      for (let k = 0; k <= ARC; k++) {
        const phi = (Math.PI * k) / ARC;
        const cphi = Math.cos(phi);
        const sphi = sign * Math.sin(phi);
        const dx = ux * cphi + t.x * sphi;
        const dy = uy * cphi + t.y * sphi;
        const dz = uz * cphi + t.z * sphi;
        arc.push(push(pt.x + dx * hw, pt.y + dy * hw, pt.z + dz * hw));
      }
      for (let k = 0; k < ARC; k++) id.push(center, arc[k + 1], arc[k]);
    };

    for (let i = 0; i < n; i++) {
      const isStart = i === 0 || pts[i].brk;
      const isEnd = i === n - 1 || (i < n - 1 && pts[i + 1].brk);
      if (isStart) cap(i, segDir(i), -1);
      if (isEnd) cap(i, segDir(i), 1);
    }

    this.geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(p), 3));
    this.geo.setIndex(new THREE.BufferAttribute(new Uint32Array(id), 1));
    this.geo.setDrawRange(0, id.length);
    this.geo.deleteAttribute('normal');
    this.geo.computeVertexNormals();
    this.geo.computeBoundingSphere();
  }

  dispose(): void {
    this.geo.dispose();
    this.mat.dispose();
  }
}

export class SlimeTrail {
  private scene: THREE.Scene;
  private fadeDuration: number;
  private sampler: GroundSampler;
  private platformUnder: PlatformUnder;
  private halfWidth: number;
  private style: SlimeStyle = 'transparent';

  private floor: Ribbon;
  private wall: Ribbon;
  private colorHex: number = SLIME_COLOR;

  private fDist = 0;
  private wDist = 0;
  private lastX = NaN;
  private lastZ = NaN;
  private lastWX = NaN;
  private lastWZ = NaN;

  private discGeo = new THREE.CircleGeometry(1, 28);
  private disc: THREE.Mesh | null = null;
  private discBase = 1;
  private discT = 0;
  private stillTime = 0;
  private readonly STILL_DELAY = 0.25;
  private readonly DISC_SHRINK_TIME = 0.9;
  private tmpDQ = new THREE.Quaternion();
  private tmpDN = new THREE.Vector3();
  private tmpDZ = new THREE.Vector3(0, 0, 1);

  /** Cipratan pendaratan: disc yang mengembang lalu memudar. */
  private splashes: Array<{ mesh: THREE.Mesh; age: number; life: number; radius: number }> = [];
  private readonly SPLASH_LIFE = 0.9;

  constructor(
    scene: THREE.Scene,
    sampler: GroundSampler,
    width = 1.21,
    fadeDuration = 2.0,
    platformUnder?: PlatformUnder,
  ) {
    this.scene = scene;
    this.sampler = sampler;
    this.platformUnder = platformUnder ?? (() => null);
    this.halfWidth = width / 2;
    this.fadeDuration = fadeDuration;
    this.floor = new Ribbon(scene);
    this.wall = new Ribbon(scene);
  }

  update(
    delta: number,
    x: number,
    y: number,
    z: number,
    active: boolean,
    carryX = 0,
    carryZ = 0,
    spread = 1,
  ): void {
    if (Number.isNaN(this.lastX)) {
      this.lastX = x;
      this.lastZ = z;
    }

    // Jarak jejak dihitung dari GERAK PEMAIN SENDIRI, bukan pergeseran
    // posisi akibat platform membawanya (carry). Berdiri di platform yang
    // meluncur tidak menyisakan jejak.
    const selfStep = Math.hypot(x - this.lastX - carryX, z - this.lastZ - carryZ);
    if (active) {
      this.fDist += selfStep;
    } else {
      this.fDist = 0;
    }
    this.lastX = x;
    this.lastZ = z;

    if (active && selfStep < 0.004) {
      this.stillTime += delta;
    } else {
      this.stillTime = 0;
    }
    this.updateIdleDisc(delta, x, y, z, active, carryX, carryZ);

    if (active && this.fDist >= SEG_GAP) {
      this.fDist = 0;
      let brk = false;
      const fs = this.floor.points;
      if (fs.length > 0) {
        const last = fs[fs.length - 1];
        brk = Math.hypot(x - last.x, z - last.z) > 1.5;
      }
      const c = this.sampler(x, z);
      // Tinggi jejak DIBATASI kaki pemain: saat pusat overlap footprint
      // (menekan sisi objek), jejak tidak boleh ikut 'naik ke atas'
      // permukaan objek. Bermain di tempat yang sah (ramp/box) tetap
      // mengikuti karena y == permukaan yang dipijak.
      const h = Math.min(c.height, y + 0.02);
      let anchor: Pt['anchor'] = null;
      const pl = this.platformUnder(x, z);
      if (pl) anchor = { platform: pl, offX: x - pl.px, offZ: z - pl.pz };
      fs.push({ x, y: h + THICK, z, age: 0, brk, nx: c.normalX, ny: 1, nz: c.normalZ, anchor, w: spread });
    }

    // Titik yang tertempel platform: ikut pergeseran platform (menempel).
    for (const pt of this.floor.points) {
      const a = pt.anchor;
      if (a) {
        pt.x = a.platform.px + a.offX;
        pt.z = a.platform.pz + a.offZ;
      }
    }

    this.age(this.floor.points, delta);
    this.age(this.wall.points, delta);
    this.ageSplashes(delta);

    this.floor.rebuild(this.halfWidth, this.fadeDuration);
    this.wall.rebuild(this.halfWidth, this.fadeDuration);
  }

  /** Cipratan mendarat: disc goo yang mengembang ke `radius` lalu memudar.
   *  Radius diskalakan dari tinggi jatuh pemanggil. */
  splash(x: number, y: number, z: number, radius: number): void {
    if (!(radius > 0) || !Number.isFinite(radius)) return;
    const mat = makeGooMaterial(0.85);
    applyGooStyle(mat, this.style);
    mat.color.setHex(this.colorHex);
    const m = new THREE.Mesh(this.discGeo, mat);
    m.frustumCulled = false;
    m.renderOrder = 2;
    const c = this.sampler(x, z);
    m.position.set(x, Math.min(c.height, y + 0.02) + 0.04, z);
    this.tmpDN.set(c.normalX, 1, c.normalZ).normalize();
    this.tmpDQ.setFromUnitVectors(this.tmpDZ, this.tmpDN);
    m.setRotationFromQuaternion(this.tmpDQ);
    m.scale.setScalar(0.001);
    m.visible = true;
    this.scene.add(m);
    this.splashes.push({ mesh: m, age: 0, life: this.SPLASH_LIFE, radius });
    if (this.splashes.length > 8) {
      const old = this.splashes.shift();
      if (old) {
        this.scene.remove(old.mesh);
        (old.mesh.material as THREE.Material).dispose();
      }
    }
  }

  private ageSplashes(delta: number): void {
    for (let i = this.splashes.length - 1; i >= 0; i--) {
      const s = this.splashes[i];
      s.age += delta;
      const t = s.age / s.life;
      if (t >= 1) {
        this.scene.remove(s.mesh);
        (s.mesh.material as THREE.Material).dispose();
        this.splashes.splice(i, 1);
        continue;
      }
      // Cepat mengembang di awal, lalu memudar: 0 -> radius dalam ~40% waktu.
      const grow = Math.min(1, t / 0.4);
      const ease = 1 - (1 - grow) * (1 - grow);
      s.mesh.scale.setScalar(Math.max(0.001, s.radius * ease));
      (s.mesh.material as THREE.MeshPhysicalMaterial).opacity =
        this.discBaseOpacity() * (1 - t) * 0.9;
    }
  }

  addWall(x: number, y: number, z: number, nx: number, ny: number, nz: number, active: boolean): void {
    if (!active) {
      this.wDist = 0;
      return;
    }
    if (Number.isNaN(this.lastWX)) {
      this.lastWX = x;
      this.lastWZ = z;
    }
    const dx = x - this.lastWX;
    const dz = z - this.lastWZ;
    this.wDist += Math.hypot(dx, dz);
    this.lastWX = x;
    this.lastWZ = z;

    if (this.wDist >= SEG_GAP) {
      this.wDist = 0;
      let brk = false;
      const ws = this.wall.points;
      if (ws.length > 0) {
        const last = ws[ws.length - 1];
        brk = Math.hypot(x - last.x, z - last.z) > 1.5;
      }
      ws.push({ x: x + nx * 0.03, y: y + ny * 0.03, z: z + nz * 0.03, age: 0, brk, nx, ny, nz, anchor: null, w: 1 });
    }
  }

  setMode(mode: 'flat' | '3d'): void {
    this.floor.setMode(mode);
    this.wall.setMode(mode);
    if (this.disc) {
      applyGooStyle(this.disc.material as THREE.MeshPhysicalMaterial, this.style);
    }
  }

  setStyle(style: SlimeStyle): void {
    this.style = style;
    this.floor.setStyle(style);
    this.wall.setStyle(style);
    if (this.disc) {
      applyGooStyle(this.disc.material as THREE.MeshPhysicalMaterial, this.style);
    }
  }

  /** Warna goo jejak mengikuti warna slime pemilik. */
  setColor(hex: number): void {
    this.colorHex = hex;
    this.floor.setColor(hex);
    this.wall.setColor(hex);
    if (this.disc) {
      (this.disc.material as THREE.MeshPhysicalMaterial).color.setHex(hex);
    }
  }

  setWidth(width: number): void {
    this.halfWidth = Math.max(0.1, width / 2);
  }

  /** Bersihkan jejak (untuk ganti stage). */
  clear(): void {
    this.floor.points.length = 0;
    this.wall.points.length = 0;
    this.fDist = 0;
    this.wDist = 0;
    this.lastX = NaN;
    this.lastZ = NaN;
    this.lastWX = NaN;
    this.lastWZ = NaN;
    this.stillTime = 0;
    this.discT = 0;
    if (this.disc) this.disc.visible = false;
    for (const s of this.splashes) {
      this.scene.remove(s.mesh);
      (s.mesh.material as THREE.Material).dispose();
    }
    this.splashes.length = 0;
  }

  private age(arr: Pt[], delta: number): void {
    for (let i = arr.length - 1; i >= 0; i--) {
      arr[i].age += delta;
      if (arr[i].age > this.fadeDuration) {
        arr.splice(i, 1);
      }
    }
  }

  private updateIdleDisc(
    delta: number,
    x: number,
    y: number,
    z: number,
    active: boolean,
    carryX = 0,
    carryZ = 0,
  ): void {
    // Diam yang 'diangkut' platform (tanpa gerak sendiri) bukan idle murni:
    // disc tidak boleh muncul hanya karena platform membawa pemain.
    const carried = Math.hypot(carryX, carryZ) > 0.002;
    if (active && this.stillTime > this.STILL_DELAY && !carried) {
      if (!this.disc) {
        const mat = makeGooMaterial(0.7);
        this.disc = new THREE.Mesh(this.discGeo, mat);
        this.disc.frustumCulled = false;
        this.disc.renderOrder = 2;
        this.scene.add(this.disc);
        applyGooStyle(mat, this.style);
        mat.color.setHex(this.colorHex);
      }
      const c = this.sampler(x, z);
      this.disc.visible = true;
      // Sama seperti ribbon: disc tak boleh 'naik ke atas' objek saat
      // pusat overlap footprint tapi kaki masih di permukaan rendah.
      this.disc.position.set(x, Math.min(c.height, y + 0.02) + 0.035, z);
      this.tmpDN.set(c.normalX, 1, c.normalZ).normalize();
      this.tmpDQ.setFromUnitVectors(this.tmpDZ, this.tmpDN);
      this.disc.setRotationFromQuaternion(this.tmpDQ);
      this.discBase = Math.max(0.1, this.halfWidth);
      this.disc.scale.setScalar(this.discBase);
      (this.disc.material as THREE.MeshPhysicalMaterial).opacity = this.discBaseOpacity();
      this.discT = 1;
      return;
    }

    if (this.disc && this.disc.visible) {
      this.discT -= delta / this.DISC_SHRINK_TIME;
      if (this.discT <= 0) {
        this.discT = 0;
        this.disc.visible = false;
      } else {
        const e = this.discT * this.discT;
        this.disc.scale.setScalar(Math.max(0.001, this.discBase * e));
        (this.disc.material as THREE.MeshPhysicalMaterial).opacity = this.discBaseOpacity() * e;
      }
    }
  }

  private discBaseOpacity(): number {
    return this.style === 'cartoon' ? 1 : 0.92;
  }

  dispose(): void {
    this.floor.dispose();
    this.wall.dispose();
    if (this.disc) {
      this.scene.remove(this.disc);
      (this.disc.material as THREE.Material).dispose();
      this.disc = null;
    }
    for (const s of this.splashes) {
      this.scene.remove(s.mesh);
      (s.mesh.material as THREE.Material).dispose();
    }
    this.splashes.length = 0;
    this.discGeo.dispose();
  }
}